/**
 * Stone Investment Area Stats
 * 
 * Area statistics section with Stone Investment's luxury aesthetic:
 * - Elegant stat cards
 * - Weather integration
 * - Market statistics
 */

'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { Sun, Cloud, CloudRain, Wind, Droplets } from 'lucide-react';
import type { AreaWeatherData, AreaMarketStats } from '@routeroyal/theme-core';

export interface StoneAreaStatsProps {
  /**
   * Area data
   */
  area: {
    name: string;
    propertyCount?: number;
    averagePrice?: number;
    pricePerSqm?: number;
    currency?: string;
  };
  /**
   * Weather data
   */
  weather?: AreaWeatherData | null;
  /**
   * Whether weather is loading
   */
  weatherLoading?: boolean;
  /**
   * Market statistics
   */
  marketStats?: AreaMarketStats | undefined;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Get weather icon based on condition
 */
function WeatherIcon({ condition }: { condition: string }) {
  const conditionLower = condition?.toLowerCase() || '';
  
  if (conditionLower.includes('rain') || conditionLower.includes('shower')) {
    return <CloudRain size={24} className="text-stone-500" strokeWidth={1.5} />;
  }
  if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
    return <Cloud size={24} className="text-stone-500" strokeWidth={1.5} />;
  }
  return <Sun size={24} className="text-stone-500" strokeWidth={1.5} />;
}

/**
 * StoneAreaStats - Area statistics section
 */
export function StoneAreaStats({
  area,
  weather,
  weatherLoading = false,
  marketStats,
  messages = {},
  className = '',
}: StoneAreaStatsProps) {
  return (
    <div className={`bg-stone-100 p-8 md:p-12 ${className}`}>
      {/* Section Header */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-3">
          {messages.statsLabel || 'Area Insights'}
        </p>
        <h2 
          className="font-serif text-3xl md:text-4xl text-stone-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {messages.statsTitle || 'At a Glance'}
        </h2>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {/* Property Count */}
        {area.propertyCount !== undefined && (
          <div className="bg-white p-6">
            <span className="font-serif text-4xl text-stone-900">
              {area.propertyCount}
            </span>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-2">
              {messages.properties || 'Properties'}
            </p>
          </div>
        )}
        
        {/* Average Price */}
        {area.averagePrice !== undefined && (
          <div className="bg-white p-6">
            <FormatPrice
              amount={area.averagePrice}
              currency={area.currency || DEFAULT_CURRENCY}
              size="lg"
              className="font-serif text-stone-900"
            />
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-2">
              {messages.avgPrice || 'Avg. Price'}
            </p>
          </div>
        )}
        
        {/* Price per sqm */}
        {area.pricePerSqm !== undefined && (
          <div className="bg-white p-6">
            <FormatPrice
              amount={area.pricePerSqm}
              currency={area.currency || DEFAULT_CURRENCY}
              size="lg"
              className="font-serif text-stone-900"
            />
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-2">
              {messages.pricePerSqm || 'Per m²'}
            </p>
          </div>
        )}
        
        {/* Market Stats */}
        {marketStats?.avgDaysOnMarket !== undefined && (
          <div className="bg-white p-6">
            <span className="font-serif text-4xl text-stone-900">
              {marketStats.avgDaysOnMarket}
            </span>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mt-2">
              {messages.daysOnMarket || 'Avg. Days on Market'}
            </p>
          </div>
        )}
      </div>
      
      {/* Weather Section */}
      {(weather || weatherLoading) && (
        <div className="mt-8 pt-8 border-t border-stone-200">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
            {messages.currentWeather || 'Current Weather'}
          </p>
          
          {weatherLoading ? (
            <div className="flex items-center gap-4 animate-pulse">
              <div className="w-12 h-12 bg-stone-200" />
              <div className="flex-1">
                <div className="h-6 bg-stone-200 w-20 mb-2" />
                <div className="h-4 bg-stone-200 w-32" />
              </div>
            </div>
          ) : weather ? (
            <div className="flex flex-wrap items-center gap-8">
              {/* Temperature */}
              <div className="flex items-center gap-4">
                <WeatherIcon condition={weather.condition || 'Clear'} />
                <div>
                  <span className="font-serif text-3xl text-stone-900">
                    {weather.temperature}°
                  </span>
                  <p className="text-xs text-stone-500 font-sans">
                    {weather.condition || 'Clear'}
                  </p>
                </div>
              </div>
              
              {/* Humidity */}
              {weather.humidity !== undefined && (
                <div className="flex items-center gap-2">
                  <Droplets size={18} className="text-stone-400" strokeWidth={1.5} />
                  <span className="font-sans text-sm text-stone-600">
                    {weather.humidity}%
                  </span>
                </div>
              )}
              
              {/* Wind */}
              {weather.windSpeed !== undefined && (
                <div className="flex items-center gap-2">
                  <Wind size={18} className="text-stone-400" strokeWidth={1.5} />
                  <span className="font-sans text-sm text-stone-600">
                    {weather.windSpeed} km/h
                  </span>
                </div>
              )}
              
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default StoneAreaStats;
